import { z } from "zod"

import { registrationIdRequestDtoTypeEnum } from "~/gen/api"

/** Exported for use in useRegistrationIdSchema (translated validation). */
export const vatRegex = /^[A-Z]{2}\d{2}\d{9}$/
export const eoriRegex = /^[A-Z]{2}\d{14}$/
export const glnRegex = /^\d{13}$/
export const dunsRegex = /^\d{9}$/
export const rcsRegex = /^RCS .+ [AB] \d{9}$/

/** Validates a single registration ID entry (type + value) when adding to the list. */
export const registrationIdSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string().optional(),
    type: z.literal("VAT"),
    value: z
      .string()
      .regex(vatRegex, {
        error:
          "VAT registration ID must be in format: country code (e.g., FR) + 2-digit key + 9-digit SIREN number.",
      })
      .describe(
        "VAT registration ID must be in format: country code (e.g., FR) + 2-digit key + 9-digit SIREN number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("EORI"),
    value: z
      .string()
      .regex(eoriRegex, {
        error:
          "EORI registration ID must be in format: country code (e.g., FR) + 14-digit number.",
      })
      .describe(
        "EORI registration ID must be in format: country code (e.g., FR) + 14-digit number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("GLN"),
    value: z
      .string()
      .min(13, {
        error: "GLN registration ID must be in format: 13-digit number.",
      })
      .describe("GLN registration ID must be in format: 13-digit number."),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("DUNS"),
    value: z
      .string()
      .regex(dunsRegex, {
        error: "DUNS registration ID must be in format: 9-digit number.",
      })
      .describe("DUNS registration ID must be in format: 9-digit number."),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("RCS"),
    value: z
      .string()
      .regex(rcsRegex, {
        error:
          "RCS registration ID must be in format: RCS + space + company name + space + [A,B] + space + 9-digit number.",
      })
      .describe(
        "RCS registration ID must be in format: RCS + space + company name + space + [A,B] + space + 9-digit number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("OTHER"),
    value: z
      .string()
      .max(32, {
        error: "Other registration ID must be less than 32 characters.",
      })
      .describe("Other registration ID must be less than 32 characters."),
  }),
])

export type RegistrationIdEntry = z.infer<typeof registrationIdSchema>

/** Form/API shape for one item in the list (registrationId key used when submitting). */
export const registrationIdItemFormSchema = z.object({
  id: z.string().optional(),
  registrationId: z.string(),
  type: z.enum(registrationIdRequestDtoTypeEnum),
})

export type RegistrationIdItemForm = z.infer<
  typeof registrationIdItemFormSchema
>
